---
title: "Drafting outreach to prospects"
description: "Draft sourcing outreach in Chosen — an AI-written first message personalized per prospect that you edit freely and send from your own connected inbox."
category: "sourcing"
keywords:
  - "outreach draft"
  - "recruiting outreach email"
  - "AI cold outreach"
  - "personalized recruiter email"
  - "sourcing email"
schema: "HowTo"
nav:
  section: "guides"
  order: 30
  parent: "/guides/sourcing"
---

Chosen drafts a first outreach message for each prospect, built from what it knows about them. You read it, edit anything that's off, and send — all from your own connected inbox. The draft is a starting point, not a finished email.

## Generate an AI draft

An AI draft reads what Chosen has on the prospect — their role, recent company, the shape of their career — and writes a first message around it. Each draft is different because each prospect is.

<Steps>
  <li>Open a prospect and find the **Contact & outreach** section in the drawer.</li>
  <li>Click **Compose outreach**, then **Generate draft**. Chosen writes a subject and body.</li>
  <li>With a job selected in the left sidebar, turn on **Personalize using selected role** — the draft then pitches that specific role, using its title and description.</li>
  <li>Read it. Edit anything that's off, or click **Regenerate** for a fresh take.</li>
</Steps>

The AI draft earns its keep on a varied list — different seniorities, different industries, a different reason each person is a fit. Writing thirty of those by hand is the tax that quietly kills most sourcing campaigns. It does not, however, remove the need to read what got written.

## Or write it yourself

You don't have to start from an AI draft. The composer is an ordinary text box — write the message yourself, or generate a draft and rewrite it until it sounds like you. Type `{first_name}` anywhere in the message and Chosen fills in each prospect's first name as it sends.

<Callout type="warning" title="A first name is not personalization">
`Hi {first_name}` is a mail merge, and prospects can tell. A message works when the *body* genuinely applies to the person reading it. If you keep wishing you could say something different to each prospect, that's the signal to generate a per-prospect AI draft instead of reusing one block of text.
</Callout>

## Everything sends from your inbox

Outreach goes out through your own connected [Gmail](/getting-started/integrations/gmail), from your real address — not a `noreply@`. Replies thread back to your inbox where you'd expect them, and the prospect is talking to a person they can build a relationship with, which is the entire point of sourcing.

No Gmail connected means no sending — the composer says **Connect Gmail to send** until you connect one.

## When it gets it wrong

An AI draft is built from a prospect's parsed profile, so a thin or messy profile produces a thin, generic message. If a draft reads like it could go to anyone, the profile probably didn't give it much to work with — check the [experience timeline](/guides/sourcing/experience-timeline) and edit the draft by hand.

Drafts can also be confidently wrong about a detail — a former employer, an inferred specialty. You are the last check before the message sends. Once it's ready, [sending outreach](/guides/sourcing/outreach-sending) covers what happens next.
